import React from "react";
import { formatNumber } from "@/lib/utils";
import { getCompanyConfig } from "@/config/company";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle } from "@/components/shared/print";
import {
  QcPrintConclusion,
  QcPrintCriteriaTable,
  QcPrintDefects,
  QcPrintInfoGrid,
  QcPrintSamplingRow,
  checklistOf,
  packingSummary,
} from "./qc-print-blocks";

interface CoAPrintTemplateProps {
  data?: any;
  config?: any;
}

export default function CoAPrintTemplate({
  data = {},
  config,
}: CoAPrintTemplateProps) {
  const c = getCompanyConfig(config);

  const isDeliveryDoc = Boolean(data?.deliveryId || Array.isArray(data?.deliveryItems));
  const primaryFqc = isDeliveryDoc
    ? Array.isArray(data?.qcFinals) && data.qcFinals.length > 0
      ? data.qcFinals[0]
      : null
    : data;

  const coaNo =
    primaryFqc?.coaNo ||
    (primaryFqc?.finalQcId
      ? String(primaryFqc.finalQcId).replace(/^QCF-/, "COA-")
      : data?.deliveryId
        ? String(data.deliveryId).replace(/^DEL-/, "COA-")
        : `COA-${new Date().getFullYear()}`);

  const issueDate =
    primaryFqc?.coaIssuedAt ||
    data?.deliveryDate ||
    primaryFqc?.checkTime ||
    data?.createdAt ||
    new Date();

  // Rule 6: Legal Full Name of Customer
  const customerFullName =
    data?.customer?.name ||
    primaryFqc?.customer?.name ||
    primaryFqc?.productionOrder?.customer?.name ||
    data?.customerName ||
    "—";

  const customerAddress =
    data?.address ||
    data?.deliveryAddress ||
    data?.customer?.address ||
    primaryFqc?.customer?.address ||
    primaryFqc?.productionOrder?.customer?.address ||
    "—";

  const deliveryCode =
    data?.deliveryId ||
    data?.deliveryInfo?.latestDelivery?.deliveryId ||
    data?.deliveryInfo?.deliveryCode ||
    "—";

  const orderCode =
    data?.order?.orderId ||
    primaryFqc?.order?.orderId ||
    primaryFqc?.productionOrder?.salesOrder?.orderId ||
    "—";

  const lsxCode =
    primaryFqc?.productionOrder?.orderId ||
    (Array.isArray(data?.deliveryItems) && data.deliveryItems[0]?.poNumber) ||
    "—";

  // Số lô, quy cách thùng lấy từ Phiếu đóng gói của lô (Sản xuất)
  const lotNumber = primaryFqc?.packingSlip?.lotNumber || lsxCode || deliveryCode || "—";

  const deliveryItems = Array.isArray(data?.deliveryItems)
    ? data.deliveryItems.filter((it: any) => !it.isServiceItem)
    : [];

  const productName =
    primaryFqc?.productName ||
    primaryFqc?.productionOrder?.productName ||
    primaryFqc?.product?.productName ||
    (deliveryItems.length > 0
      ? deliveryItems.map((it: any) => it.productName).filter(Boolean).join("; ")
      : "—");

  const shippedOrPassedQty = isDeliveryDoc
    ? deliveryItems.reduce((sum: number, it: any) => sum + Number(it.deliveredQty || it.quantity || 0), 0) ||
      Number(primaryFqc?.qtyPassed || 0)
    : Number(primaryFqc?.qtyPassed || primaryFqc?.qtyChecked || 0);

  const printingColor = primaryFqc?.printingIpqcSummary || data?.printingIpqcSummary || null;

  // Chỉ tiêu: bản chụp trên biên bản FQC. Phiếu cũ không có bảng chỉ tiêu thì dùng số đo ô cứng đã lưu (có thật),
  // không tự điền "Đạt chuẩn".
  const rawChecklist = checklistOf(primaryFqc?.checklist);
  const legacyRows = [
    (primaryFqc?.actualLength || primaryFqc?.actualWidth || primaryFqc?.actualHeight) && {
      id: "legacy_dim",
      name: "Kích thước (D × R × C)",
      type: "QUALITATIVE",
      standard: "Theo bản vẽ / mẫu duyệt",
      result: "PASS",
      note: `${primaryFqc?.actualLength ?? "—"} × ${primaryFqc?.actualWidth ?? "—"} × ${primaryFqc?.actualHeight ?? "—"} mm`,
    },
    primaryFqc?.moisture !== undefined && primaryFqc?.moisture !== null && {
      id: "legacy_moisture",
      name: "Độ ẩm",
      type: "QUANTITATIVE",
      unit: "%",
      measuredValue: primaryFqc.moisture,
      result: "PASS",
    },
    primaryFqc?.barcodeGrade && {
      id: "legacy_barcode",
      name: "Mã vạch quét được",
      type: "QUALITATIVE",
      standard: "≥ hạng B",
      result: String(primaryFqc.barcodeGrade).toUpperCase() === "ANSI_F" ? "FAIL" : "PASS",
      note: String(primaryFqc.barcodeGrade).replace(/^ANSI_/, "Hạng "),
    },
    primaryFqc?.fittingTestResult && {
      id: "legacy_fitting",
      name: "Dựng hộp & mép keo",
      type: "QUALITATIVE",
      standard: "Vuông vắn, rách xơ giấy ≥ 85%",
      result: /^FAIL/i.test(primaryFqc.fittingTestResult) ? "FAIL" : "PASS",
    },
    primaryFqc?.dropTestResult && {
      id: "legacy_drop",
      name: "Thả rơi",
      type: "QUALITATIVE",
      standard: "Thùng & hộp nguyên vẹn",
      result: /^FAIL/i.test(primaryFqc.dropTestResult) ? "FAIL" : "PASS",
    },
  ].filter(Boolean);
  const criteria = rawChecklist.length > 0 ? rawChecklist : legacyRows;

  return (
    <div
      className="p-[8mm] text-black leading-tight text-[10pt] bg-white w-full max-w-[210mm] mx-auto"
      style={{ fontFamily: "'Times New Roman', Times, serif" }}
      id="print-area"
    >
      {/* 1. ISO COMPANY HEADER (Rule 7 Brand Seal from useCompanyConfig) */}
      <ISOPrintHeader config={config} />

      {/* 2. DOCUMENT TITLE */}
      <PrintDocumentTitle
        title="PHIẾU CHỨNG NHẬN CHẤT LƯỢNG (CoA)"
        docNo={coaNo}
        docNoLabel="Số CoA"
        date={issueDate}
        dateLabel="Ngày cấp"
      />

      <QcPrintInfoGrid
        items={[
          ["Khách hàng", customerFullName],
          ["Địa chỉ giao", customerAddress],
          ["Thành phẩm", productName],
          ["Mẫu chuẩn", primaryFqc?.referenceSample],
          ["Phiếu giao / ĐH", `${deliveryCode} / ${orderCode}`],
          ["Lệnh SX / Số lô", `${lsxCode} / ${lotNumber}`],
          ["SL xuất", formatNumber(shippedOrPassedQty)],
          ["Đóng gói", packingSummary(shippedOrPassedQty, Number(primaryFqc?.packingSlip?.pcsPerBox || 0))],
        ]}
      />

      {deliveryItems.length > 0 && (
        <div className="mb-2">
          <div className="mb-0.5 text-[9.5pt] font-bold uppercase">Thành phẩm xuất giao</div>
          <table className="qc-print-table w-full border-collapse border border-black text-[9pt]">
            <thead>
              <tr className="bg-slate-100 font-bold text-center">
                <th className="border border-black py-0.5 px-1" style={{ width: "6%" }}>#</th>
                <th className="border border-black py-0.5 px-1 text-left">Sản phẩm</th>
                <th className="border border-black py-0.5 px-1" style={{ width: "18%" }}>Lệnh SX</th>
                <th className="border border-black py-0.5 px-1" style={{ width: "10%" }}>ĐVT</th>
                <th className="border border-black py-0.5 px-1 text-right" style={{ width: "14%" }}>SL xuất</th>
              </tr>
            </thead>
            <tbody>
              {deliveryItems.slice(0, 6).map((it: any, idx: number) => (
                <tr key={idx}>
                  <td className="border border-black py-0.5 px-1 text-center">{idx + 1}</td>
                  <td className="border border-black py-0.5 px-1 font-semibold">{it.productName}</td>
                  <td className="border border-black py-0.5 px-1 text-center font-mono">{it.poNumber || lsxCode}</td>
                  <td className="border border-black py-0.5 px-1 text-center">{it.unit || "Hộp"}</td>
                  <td className="border border-black py-0.5 px-1 text-right font-mono font-bold">
                    {formatNumber(it.deliveredQty || it.quantity || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {primaryFqc && (
        <QcPrintSamplingRow
          lotSize={primaryFqc.lotSize ?? primaryFqc.qtyChecked}
          mode={primaryFqc.inspectionMode}
          level={primaryFqc.aqlLevel}
          aqlMajor={primaryFqc.aqlStandard}
          aqlMinor={primaryFqc.aqlMinor}
          sampleSize={primaryFqc.sampleSize}
          acMajor={primaryFqc.acValue}
          reMajor={primaryFqc.reValue}
          acMinor={primaryFqc.acMinor}
          reMinor={primaryFqc.reMinor}
        />
      )}

      <QcPrintCriteriaTable items={criteria} title="Kết quả kiểm tra xuất xưởng" />

      {printingColor && (
        <div className="mb-2 text-[8.5pt]">
          <b>Màu in (IPQC):</b> ΔE = {printingColor.deltaE ?? "—"} (L*{printingColor.labActL ?? "—"} a*{printingColor.labActA ?? "—"} b*
          {printingColor.labActB ?? "—"})
        </div>
      )}

      {primaryFqc && <QcPrintDefects items={primaryFqc.defectDetails} />}

      {primaryFqc && (
        <QcPrintConclusion
          status={primaryFqc.status}
          counts={{
            critical: primaryFqc.criticalDefects || 0,
            major: primaryFqc.majorDefects || 0,
            minor: primaryFqc.minorDefects || 0,
          }}
          reMajor={primaryFqc.reValue}
          reMinor={primaryFqc.reMinor}
          overrideReason={primaryFqc.overrideReason}
        />
      )}

      {/* 6. CONFORMANCE STATEMENT */}
      <div className="border border-black px-1.5 py-1 mb-2 bg-slate-50/70 text-[9pt]">
        <strong>Xác nhận xuất xưởng:</strong> Phòng QA/QC <strong>{c.companyName}</strong> chứng nhận lô hàng trên đã hoàn tất kiểm định IQC – IPQC – FQC theo tiêu chuẩn ISO 2859-1, đáp ứng yêu cầu kỹ thuật &amp; mẫu duyệt, đủ điều kiện xuất kho.
      </div>

      {/* 7. SIGNATURES */}
      <div className="grid grid-cols-3 gap-4 mt-2 text-center text-[9.5pt] break-inside-avoid">
        <div>
          <div className="font-bold uppercase">KCS Kiểm Tra</div>
          <div className="italic text-[9.5px] text-slate-600">(Ký, ghi rõ họ tên)</div>
          <div className="h-12" />
          <div className="font-bold">{primaryFqc?.inspector?.fullName || data?.createdByName || ""}</div>
        </div>
        <div>
          <div className="font-bold uppercase">Trưởng Phòng QA/QC</div>
          <div className="italic text-[9.5px] text-slate-600">(Ký duyệt chất lượng)</div>
          <div className="h-12" />
          <div className="font-bold">&nbsp;</div>
        </div>
        <div>
          <div className="font-bold uppercase">Đại Diện Ban Giám Đốc</div>
          <div className="italic text-[9.5px] text-slate-600">(Ký tên &amp; đóng dấu)</div>
          <div className="h-12" />
          {/* cấu hình công ty chưa có người đại diện: để trống cho ký tay */}
          <div className="font-bold">&nbsp;</div>
        </div>
      </div>
    </div>
  );
}
